import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { WarningModalIcon } from '@/assets/static/icons/warning-modal-icon';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { DELETE_TENANT_MUTATION } from '../queries';
import { IAdvisoryFirms, ITenant } from '../types';

interface DeleteTenantModalProps {
  isOpen: boolean;
  tenant: ITenant;
  refetchTenants: () => Promise<ApolloQueryResult<{ advisoryFirms: IAdvisoryFirms }>>;
  onClose: () => void;
}

const DeleteTenantModal = ({ isOpen, tenant, refetchTenants, onClose }: DeleteTenantModalProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [deleteTenant] = useMutation(DELETE_TENANT_MUTATION);

  const onSave = async () => {
    try {
      setErrorMessage(null);
      setLoading(true);

      await deleteTenant({ variables: { data: { id: tenant?.id } } });

      await refetchTenants();
      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: !!errorMessage ? 'Done' : 'Confirm',
        onClick: !!errorMessage ? onClose : onSave,
        disabled: !!loading
      }}
      showCancelButton={!loading && !errorMessage}
      isUseOutsideClick={!loading}
      showCloseIcon={!loading}
    >
      {!!errorMessage ? (
        <WarningWrapper>
          <WarningModalIcon width={60} height={61} fill={themeVar('font.disabled')} />
          <p>This tenant cannot be deleted because it has data associated with it.</p>
        </WarningWrapper>
      ) : (
        <ModalBody>
          <DeleteBucketIcon fill={themeVar('font.disabled')} />
          {loading ? (
            <ModalLoader customText={`Deleting ${tenant?.name}...`} />
          ) : (
            <>
              <ModalTitle>
                Please confirm you want to delete <b>{tenant?.name}</b>.
              </ModalTitle>
              {errorMessage && (
                <ErrorBannerWrapper>
                  <Banner
                    icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                    title="Error"
                    description={errorMessage}
                    bgColor={themeVar('context.error')}
                  />
                </ErrorBannerWrapper>
              )}
              <ModalDescription>Data will be permanently lost upon deletion. There is no undo for this operation.</ModalDescription>
            </>
          )}
        </ModalBody>
      )}
    </ModalWrappedContent>
  );
};

export default DeleteTenantModal;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-style: normal;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;

const WarningWrapper = styled.div`
  text-align: center;
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;
